/**
 * BpmnValidator — validates a LogicFlow graph against BPMN 2.0 structural rules.
 *
 * Usage:
 *   const validator = new BpmnValidator(lf, i18n);
 *   const { errors, warnings } = validator.validate();
 *   validator.showOverlays();   // highlight invalid elements
 *   validator.clearOverlays();  // remove highlights
 */

/* ── type sets ──────────────────────────────────────────────────────── */

const EVENT_TYPES = new Set([
  'epx:StartEvent', 'epx:EndEvent',
  'epx:IntermediateEvent', 'epx:IntermediateThrowEvent',
  'epx:BoundaryEvent',
]);

const ACTIVITY_TYPES = new Set([
  'epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask', 'epx:ManualTask',
  'epx:SendTask', 'epx:ReceiveTask', 'epx:BusinessRuleTask',
  'epx:CallActivity', 'epx:SubProcess', 'epx:Transaction',
]);

const GATEWAY_TYPES = new Set([
  'epx:ExclusiveGateway', 'epx:ParallelGateway', 'epx:InclusiveGateway',
  'epx:ComplexGateway', 'epx:EventBasedGateway',
]);

const FLOW_NODE_TYPES = new Set([...EVENT_TYPES, ...ACTIVITY_TYPES, ...GATEWAY_TYPES]);

const DATA_TYPES = new Set(['epx:DataObjectReference', 'epx:DataStoreReference']);
const ARTIFACT_TYPES = new Set(['epx:Group', 'epx:TextAnnotation']);
const NON_FLOW_TYPES = new Set([...DATA_TYPES, ...ARTIFACT_TYPES, 'epx:Participant', 'epx:Lane']);

// After Event-Based Gateway only these targets are valid
const EVENT_GW_VALID_TARGETS = new Set([
  'epx:IntermediateEvent', // catch
  'epx:ReceiveTask',
]);

/* ── helpers ────────────────────────────────────────────────────────── */

function nodeName(node) {
  return (node.properties && node.properties.name) || node.text?.value || node.id;
}

function isSequenceFlow(edge) {
  return edge.type === 'epx:SequenceFlow';
}

function isMessageFlow(edge) {
  return edge.type === 'epx:MessageFlow';
}

/* ── Validator class ────────────────────────────────────────────────── */

export class BpmnValidator {
  /**
   * @param {import('@logicflow/core').default} lf  LogicFlow instance
   * @param {object} i18n  i18n dictionary (en.json / es.json)
   */
  constructor(lf, i18n = {}) {
    this._lf = lf;
    this._t = i18n.validation || {};
    this._overlayIds = [];
  }

  /* ─── public API ─────────────────────────────────────────────────── */

  /**
   * Run all rules and return structured results.
   * @returns {{ errors: Array<{id:string, message:string, type:string}>, warnings: Array<{id:string, message:string, type:string}> }}
   */
  validate() {
    const { nodes, edges } = this._lf.getGraphData();
    const errors = [];
    const warnings = [];

    // Build lookup maps
    const nodeMap = new Map();                      // id → node
    const incomingSeq = new Map();                  // nodeId → [edge]
    const outgoingSeq = new Map();                  // nodeId → [edge]
    const incomingMsg = new Map();                  // nodeId → [edge]
    const outgoingMsg = new Map();                  // nodeId → [edge]
    const participantNodes = [];                    // Participant nodes
    const participantBounds = [];                   // {id, x, y, w, h}

    for (const n of nodes) {
      nodeMap.set(n.id, n);
      incomingSeq.set(n.id, []);
      outgoingSeq.set(n.id, []);
      incomingMsg.set(n.id, []);
      outgoingMsg.set(n.id, []);
      if (n.type === 'epx:Participant') {
        participantNodes.push(n);
        const w = (n.properties && n.properties.width) || 600;
        const h = (n.properties && n.properties.height) || 250;
        participantBounds.push({
          id: n.id,
          left: n.x - w / 2, right: n.x + w / 2,
          top: n.y - h / 2, bottom: n.y + h / 2,
        });
      }
    }

    for (const e of edges) {
      if (isSequenceFlow(e)) {
        if (outgoingSeq.has(e.sourceNodeId)) outgoingSeq.get(e.sourceNodeId).push(e);
        if (incomingSeq.has(e.targetNodeId)) incomingSeq.get(e.targetNodeId).push(e);
      } else if (isMessageFlow(e)) {
        if (outgoingMsg.has(e.sourceNodeId)) outgoingMsg.get(e.sourceNodeId).push(e);
        if (incomingMsg.has(e.targetNodeId)) incomingMsg.get(e.targetNodeId).push(e);
      }
    }

    // Helper to find which pool a node belongs to
    const poolOf = (node) => {
      if (node.type === 'epx:Participant' || node.type === 'epx:Lane') return node.id;
      for (const pb of participantBounds) {
        if (node.x >= pb.left && node.x <= pb.right && node.y >= pb.top && node.y <= pb.bottom) {
          return pb.id;
        }
      }
      return null;
    };

    const t = (key, fallback) => this._t[key] || fallback;

    /* ── Rule 1: Start Event — no incoming sequence flows ─────────── */
    for (const n of nodes) {
      if (n.type !== 'epx:StartEvent') continue;
      const inc = incomingSeq.get(n.id) || [];
      if (inc.length > 0) {
        errors.push({
          id: n.id,
          message: t('startNoIncoming', `"${nodeName(n)}": Start Events must not have incoming sequence flows`),
          type: 'error',
        });
      }
    }

    /* ── Rule 2: End Event — no outgoing sequence flows ───────────── */
    for (const n of nodes) {
      if (n.type !== 'epx:EndEvent') continue;
      const out = outgoingSeq.get(n.id) || [];
      if (out.length > 0) {
        errors.push({
          id: n.id,
          message: t('endNoOutgoing', `"${nodeName(n)}": End Events must not have outgoing sequence flows`),
          type: 'error',
        });
      }
    }

    /* ── Rule 3: Gateway — at least 1 incoming and 1 outgoing ─────── */
    for (const n of nodes) {
      if (!GATEWAY_TYPES.has(n.type)) continue;
      const inc = (incomingSeq.get(n.id) || []).length;
      const out = (outgoingSeq.get(n.id) || []).length;
      if (inc === 0 || out === 0) {
        errors.push({
          id: n.id,
          message: t('gatewayConnections', `"${nodeName(n)}": Gateways must have at least one incoming and one outgoing sequence flow`),
          type: 'error',
        });
      }
    }

    /* ── Rule 4: Event-Based Gateway — valid targets only ─────────── */
    for (const n of nodes) {
      if (n.type !== 'epx:EventBasedGateway') continue;
      const out = outgoingSeq.get(n.id) || [];
      for (const e of out) {
        const target = nodeMap.get(e.targetNodeId);
        if (target && !EVENT_GW_VALID_TARGETS.has(target.type)) {
          errors.push({
            id: n.id,
            message: t('eventGwTargets', `"${nodeName(n)}": Event-Based Gateway may only connect to Intermediate Catch Events or Receive Tasks, not "${nodeName(target)}"`),
            type: 'error',
          });
        }
      }
    }

    /* ── Rule 5: Sequence Flow — same pool ────────────────────────── */
    if (participantNodes.length > 0) {
      for (const e of edges) {
        if (!isSequenceFlow(e)) continue;
        const src = nodeMap.get(e.sourceNodeId);
        const tgt = nodeMap.get(e.targetNodeId);
        if (!src || !tgt) continue;
        const srcPool = poolOf(src);
        const tgtPool = poolOf(tgt);
        if (srcPool && tgtPool && srcPool !== tgtPool) {
          errors.push({
            id: e.id,
            message: t('seqFlowSamePool', `Sequence Flow "${e.id}" must not cross pool boundaries (use Message Flow instead)`),
            type: 'error',
          });
        }
      }
    }

    /* ── Rule 6: Message Flow — different pools ───────────────────── */
    if (participantNodes.length > 0) {
      for (const e of edges) {
        if (!isMessageFlow(e)) continue;
        const src = nodeMap.get(e.sourceNodeId);
        const tgt = nodeMap.get(e.targetNodeId);
        if (!src || !tgt) continue;
        const srcPool = poolOf(src);
        const tgtPool = poolOf(tgt);
        if (srcPool && tgtPool && srcPool === tgtPool) {
          errors.push({
            id: e.id,
            message: t('msgFlowDiffPool', `Message Flow "${e.id}" must connect elements in different pools`),
            type: 'error',
          });
        }
      }
    }

    /* ── Rule 7: Boundary Event — must be attached to an Activity ── */
    for (const n of nodes) {
      if (n.type !== 'epx:BoundaryEvent') continue;
      const attachedTo = n.properties && n.properties.attachedToRef;
      if (!attachedTo) {
        errors.push({
          id: n.id,
          message: t('boundaryAttached', `"${nodeName(n)}": Boundary Event must be attached to an Activity`),
          type: 'error',
        });
        continue;
      }
      const host = nodeMap.get(attachedTo);
      if (host && !ACTIVITY_TYPES.has(host.type)) {
        errors.push({
          id: n.id,
          message: t('boundaryAttachedActivity', `"${nodeName(n)}": Boundary Event can only be attached to Activities, not "${host.type}"`),
          type: 'error',
        });
      }
    }

    /* ── Rule 8: Cancel End Event — only inside Transaction ───────── */
    for (const n of nodes) {
      if (n.type !== 'epx:EndEvent') continue;
      const trigger = n.properties && n.properties.trigger;
      if (trigger !== 'Cancel') continue;
      // Check if inside a Transaction (look for nearest containing Transaction)
      const isInsideTransaction = nodes.some(t => {
        if (t.type !== 'epx:Transaction') return false;
        const tw = (t.properties && t.properties.width) || 140;
        const th = (t.properties && t.properties.height) || 80;
        return (
          n.x >= t.x - tw / 2 && n.x <= t.x + tw / 2 &&
          n.y >= t.y - th / 2 && n.y <= t.y + th / 2
        );
      });
      if (!isInsideTransaction) {
        errors.push({
          id: n.id,
          message: t('cancelEndInTransaction', `"${nodeName(n)}": Cancel End Event is only allowed inside a Transaction`),
          type: 'error',
        });
      }
    }

    /* ── Rule 9: Cancel Boundary Event — only on Transaction ──────── */
    for (const n of nodes) {
      if (n.type !== 'epx:BoundaryEvent') continue;
      const trigger = n.properties && n.properties.trigger;
      if (trigger !== 'Cancel') continue;
      const attachedTo = n.properties && n.properties.attachedToRef;
      if (attachedTo) {
        const host = nodeMap.get(attachedTo);
        if (host && host.type !== 'epx:Transaction') {
          errors.push({
            id: n.id,
            message: t('cancelBoundaryOnTransaction', `"${nodeName(n)}": Cancel Boundary Event is only allowed on a Transaction`),
            type: 'error',
          });
        }
      }
    }

    /* ── Rule 10: Link Events — must exist in pairs ───────────────── */
    const linkCatch = [];  // IntermediateEvent with trigger=Link
    const linkThrow = [];  // IntermediateThrowEvent with trigger=Link
    for (const n of nodes) {
      const trigger = n.properties && n.properties.trigger;
      if (trigger !== 'Link') continue;
      if (n.type === 'epx:IntermediateEvent') linkCatch.push(n);
      if (n.type === 'epx:IntermediateThrowEvent') linkThrow.push(n);
    }
    const linkCatchNames = new Set(linkCatch.map(n => n.properties?.linkName || n.properties?.name || ''));
    const linkThrowNames = new Set(linkThrow.map(n => n.properties?.linkName || n.properties?.name || ''));
    for (const n of linkThrow) {
      const lName = n.properties?.linkName || n.properties?.name || '';
      if (lName && !linkCatchNames.has(lName)) {
        warnings.push({
          id: n.id,
          message: t('linkPairMissing', `"${nodeName(n)}": Link Throw Event "${lName}" has no matching Link Catch Event`),
          type: 'warning',
        });
      }
    }
    for (const n of linkCatch) {
      const lName = n.properties?.linkName || n.properties?.name || '';
      if (lName && !linkThrowNames.has(lName)) {
        warnings.push({
          id: n.id,
          message: t('linkPairMissing', `"${nodeName(n)}": Link Catch Event "${lName}" has no matching Link Throw Event`),
          type: 'warning',
        });
      }
    }

    /* ── Rule 11: Process should have at least one Start Event ─────── */
    const hasStartEvent = nodes.some(n => n.type === 'epx:StartEvent');
    if (nodes.length > 0 && !hasStartEvent) {
      warnings.push({
        id: '__process__',
        message: t('processNeedsStart', 'Process should have at least one Start Event'),
        type: 'warning',
      });
    }

    /* ── Rule 12: Disconnected flow nodes (no edges) ──────────────── */
    for (const n of nodes) {
      if (!FLOW_NODE_TYPES.has(n.type)) continue;
      if (n.type === 'epx:BoundaryEvent') continue; // handled by rule 7
      const inc = (incomingSeq.get(n.id) || []).length;
      const out = (outgoingSeq.get(n.id) || []).length;
      if (inc === 0 && out === 0) {
        warnings.push({
          id: n.id,
          message: t('disconnectedNode', `"${nodeName(n)}": Element has no sequence flow connections`),
          type: 'warning',
        });
      }
    }

    /* ── Rule 13: Activities should have incoming (except start-attached) */
    for (const n of nodes) {
      if (!ACTIVITY_TYPES.has(n.type)) continue;
      const inc = (incomingSeq.get(n.id) || []).length;
      if (inc === 0) {
        // Check if it has a boundary event with an incoming — skip warning
        const hasBoundaryIncoming = nodes.some(b =>
          b.type === 'epx:BoundaryEvent' &&
          b.properties?.attachedToRef === n.id
        );
        if (!hasBoundaryIncoming) {
          warnings.push({
            id: n.id,
            message: t('activityNoIncoming', `"${nodeName(n)}": Activity has no incoming sequence flow`),
            type: 'warning',
          });
        }
      }
    }

    /* ── Rule 14: Activities should have outgoing ──────────────────── */
    for (const n of nodes) {
      if (!ACTIVITY_TYPES.has(n.type)) continue;
      const out = (outgoingSeq.get(n.id) || []).length;
      if (out === 0) {
        warnings.push({
          id: n.id,
          message: t('activityNoOutgoing', `"${nodeName(n)}": Activity has no outgoing sequence flow`),
          type: 'warning',
        });
      }
    }

    /* ── Rule 15: Error End Event — should be inside a SubProcess ──── */
    for (const n of nodes) {
      if (n.type !== 'epx:EndEvent') continue;
      const trigger = n.properties && n.properties.trigger;
      if (trigger !== 'Error') continue;
      const isInsideSubProcess = nodes.some(sp => {
        if (sp.type !== 'epx:SubProcess' && sp.type !== 'epx:Transaction') return false;
        const sw = (sp.properties && sp.properties.width) || 140;
        const sh = (sp.properties && sp.properties.height) || 80;
        return (
          n.x >= sp.x - sw / 2 && n.x <= sp.x + sw / 2 &&
          n.y >= sp.y - sh / 2 && n.y <= sp.y + sh / 2
        );
      });
      if (!isInsideSubProcess) {
        warnings.push({
          id: n.id,
          message: t('errorEndInSubProcess', `"${nodeName(n)}": Error End Event is typically used inside a Sub-Process`),
          type: 'warning',
        });
      }
    }

    /* ── Rule 16: Dangling edges (source/target not found) ────────── */
    for (const e of edges) {
      if (!isSequenceFlow(e) && !isMessageFlow(e)) continue;
      if (!nodeMap.has(e.sourceNodeId)) {
        errors.push({
          id: e.id,
          message: t('danglingEdge', `Edge "${e.id}": source node not found`),
          type: 'error',
        });
      }
      if (!nodeMap.has(e.targetNodeId)) {
        errors.push({
          id: e.id,
          message: t('danglingEdge', `Edge "${e.id}": target node not found`),
          type: 'error',
        });
      }
    }

    return { errors, warnings };
  }

  /* ─── Visual overlays ────────────────────────────────────────────── */

  /**
   * Run validation and highlight invalid elements on the canvas.
   * Returns the validation results.
   */
  showOverlays() {
    this.clearOverlays();
    const results = this.validate();

    for (const item of [...results.errors, ...results.warnings]) {
      if (item.id === '__process__') continue;
      const isError = item.type === 'error';
      try {
        this._applyStyle(item.id, isError);
        this._addTooltip(item.id, item.message, isError);
      } catch (_) {
        // element might have been removed — skip silently
      }
    }

    return results;
  }

  /**
   * Remove all validation overlays / highlights.
   */
  clearOverlays() {
    for (const { id, originalStyle } of this._overlayIds) {
      try {
        this._lf.setProperties(id, { _validationStyle: undefined });
        // Restore original stroke via LogicFlow API
        const el = this._getElement(id);
        if (el) {
          const style = originalStyle || {};
          this._lf.setProperties(id, {
            _validationError: false,
            _validationWarning: false,
            _validationMessage: undefined,
          });
        }
        // Remove tooltip DOM
        this._removeTooltip(id);
      } catch (_) {
        // ignore
      }
    }
    this._overlayIds = [];
  }

  /* ─── internal helpers ───────────────────────────────────────────── */

  _getElement(id) {
    try {
      return this._lf.getNodeModelById(id) || this._lf.getEdgeModelById(id);
    } catch (_) {
      return null;
    }
  }

  _applyStyle(id, isError) {
    const el = this._getElement(id);
    if (!el) return;

    // Save original style for restoration
    const originalStyle = {};
    this._overlayIds.push({ id, originalStyle });

    // Set validation properties that shapes can read for visual feedback
    this._lf.setProperties(id, {
      _validationError: isError,
      _validationWarning: !isError,
    });
  }

  _addTooltip(id, message, isError) {
    // Find the DOM element for this node/edge on the canvas
    const container = this._lf.container;
    if (!container) return;

    const group = container.querySelector(`[data-element-id="${id}"]`)
      || container.querySelector(`g[id$="${id}"]`);

    if (!group) {
      // Fallback: add overlay via lf extension API if available
      this._overlayIds[this._overlayIds.length - 1].tooltipId = id;
      // Store message for programmatic access
      this._lf.setProperties(id, { _validationMessage: message });
      return;
    }

    // Create a small badge at the top-right of the element
    const badge = document.createElement('div');
    badge.className = `epx-validation-badge epx-validation-${isError ? 'error' : 'warning'}`;
    badge.setAttribute('data-validation-id', id);
    badge.title = message;
    badge.textContent = isError ? '!' : '?';

    // Position near the element using its SVG bounding rect
    const rect = group.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    badge.style.cssText = `
      position:absolute;
      top:${rect.top - containerRect.top - 8}px;
      left:${rect.right - containerRect.left - 8}px;
      width:16px; height:16px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      font-size:11px; font-weight:bold; cursor:help; z-index:1000;
      color:#fff;
      background:${isError ? '#f5222d' : '#faad14'};
      border:1px solid ${isError ? '#cf1322' : '#d48806'};
      pointer-events:auto;
    `;

    container.appendChild(badge);
  }

  _removeTooltip(id) {
    const container = this._lf.container;
    if (!container) return;
    const badge = container.querySelector(`[data-validation-id="${id}"]`);
    if (badge) badge.remove();
  }

  /**
   * Inject the validation CSS (call once).
   */
  static injectStyles() {
    if (document.getElementById('epx-validation-styles')) return;
    const style = document.createElement('style');
    style.id = 'epx-validation-styles';
    style.textContent = `
      .epx-validation-badge {
        box-shadow: 0 1px 4px rgba(0,0,0,.25);
        transition: transform .15s;
      }
      .epx-validation-badge:hover {
        transform: scale(1.3);
      }
    `;
    document.head.appendChild(style);
  }
}
